/**
 * @fileoverview Компонент для отображения миниатюр фотографий с возможностью выбора и удаления.
 * Используется в списке сделанных фотографий в верхней части интерфейса WebAppCam.
 * 
 * @since 1.0.0
 */

import React from 'react';
import styles from './styles.module.sass';

/**
 * Пропсы компонента Photo для отображения миниатюры фотографии.
 * 
 * @since 1.0.0
 */
interface PhotoProps {
    /** Data URL или путь к изображению для отображения */
    src: string;
    /** Флаг выбранной фотографии для визуального выделения */
    isSelected: boolean;
    /** Коллбэк для удаления фотографии из списка */
    onDelete: () => void;
    /** Коллбэк для выбора фотографии (переход в режим просмотра) */
    onClick: () => void;
}

/**
 * Компонент миниатюры фотографии с возможностью выбора и удаления.
 * 
 * Отображает изображение в компактном виде с кнопкой удаления.
 * Поддерживает визуальное выделение выбранной фотографии.
 * 
 * **Интерактивность:**
 * - Клик по изображению: выбор фотографии (переход в режим просмотра)
 * - Клик по кнопке "×": удаление фотографии с подтверждением
 * - Визуальное выделение: применяется стиль lastImage для выбранной фото
 * 
 * @param props - Пропсы компонента
 * 
 * @returns JSX элемент с миниатюрой фотографии
 * 
 * @remarks
 * Компонент используется в массиве images для отображения списка
 * сделанных фотографий. Кнопка удаления предотвращает всплытие
 * события клика для корректной обработки выбора фотографии.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * {images.map((image, index) => (
 *   <Photo
 *     key={index}
 *     src={image}
 *     isSelected={selectedPhoto === image}
 *     onClick={() => handleImageSelect(image)}
 *     onDelete={() => handleDeleteImage(index)}
 *   />
 * ))}
 * ```
 */
export const Photo: React.FC<PhotoProps> = ({ src, isSelected, onDelete, onClick }) => {
    /**
     * Обработчик удаления фотографии.
     * 
     * Предотвращает всплытие события для избежания
     * срабатывания onClick при клике на кнопку удаления.
     * 
     * @param e - Событие клика мыши
     */
    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete();
    };

    return (
        <div className={styles.photoContainer}>
            <img
                src={src}
                alt="Фото"
                className={`${styles.photo} ${isSelected ? styles.lastImage : ''}`}
                onClick={onClick}
            />
            <button
                className={styles.deleteButton}
                onClick={handleDelete}
                type="button"
            >
                ×
            </button>
        </div>
    );
};

